/*
 * Chunked upload against a GCS resumable session.
 *
 * The server opens the session (GetUploadUrl) and hands back its URI; the
 * browser PUTs the file to it one Content-Range slice at a time. GCS answers
 * 308 for every slice but the last, remembering what it has, so a dropped
 * connection costs at most the slice in flight and a closed tab can pick up
 * from the committed offset later (UploadStatus asks storage what that is).
 *
 * Progress comes from XMLHttpRequest's upload events; fetch() has none, which
 * is why a 15 GB file used to sit on "uploading" with nothing moving.
 */

export const DEFAULT_CHUNK_BYTES = 32 * 1024 * 1024; // must stay a multiple of 256 KiB

const RETRYABLE_ATTEMPTS = 8;
const SLICE_ALIGNMENT = 256 * 1024;

export class UploadSessionGoneError extends Error {
    constructor() {
        super('The upload session has expired');
        this.name = 'UploadSessionGoneError';
    }
}

export class UploadAbortedError extends Error {
    constructor() {
        super('Upload cancelled');
        this.name = 'UploadAbortedError';
    }
}

class RetryableUploadError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RetryableUploadError';
    }
}

// "bytes=0-N" from a 308 means N+1 bytes are committed. The header is not
// always readable cross-origin; callers fall back to the slice end then,
// which is what GCS commits for a whole 256 KiB-aligned slice.
export function committedFromRange(rangeHeader) {
    const match = /^bytes=0-(\d+)$/.exec(String(rangeHeader || '').trim());
    if (!match) return null;
    return Number.parseInt(match[1], 10) + 1;
}

// A slice of the file, or — when start === end — the status query GCS
// documents ("bytes */total" with no body), which also closes out a session
// whose every byte is already committed.
function putSlice({ sessionUri, blob, start, end, total, onProgress, signal }) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', sessionUri, true);
        xhr.setRequestHeader(
            'Content-Range',
            end > start ? `bytes ${start}-${end - 1}/${total}` : `bytes */${total}`
        );

        const onAbort = () => xhr.abort();
        if (signal) {
            if (signal.aborted) {
                reject(new UploadAbortedError());
                return;
            }
            signal.addEventListener('abort', onAbort, { once: true });
        }
        const cleanup = () => { if (signal) signal.removeEventListener('abort', onAbort); };

        xhr.upload.onprogress = (event) => {
            if (onProgress && event.lengthComputable) onProgress(start + event.loaded);
        };
        xhr.onload = () => {
            cleanup();
            let range = null;
            try { range = xhr.getResponseHeader('Range'); } catch { /* header not exposed */ }
            resolve({ status: xhr.status, range });
        };
        xhr.onerror = () => { cleanup(); reject(new RetryableUploadError('Network error during upload')); };
        xhr.ontimeout = () => { cleanup(); reject(new RetryableUploadError('Upload timed out')); };
        xhr.onabort = () => { cleanup(); reject(new UploadAbortedError()); };
        xhr.send(end > start ? blob : null);
    });
}

const sleep = (ms, signal) => new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    if (signal) {
        signal.addEventListener('abort', () => { clearTimeout(timer); reject(new UploadAbortedError()); }, { once: true });
    }
});

/*
 * Send `file` to `sessionUri` starting at `offset`. Resolves when GCS has the
 * whole object; rejects with UploadSessionGoneError (start over with a new
 * session), UploadAbortedError, or a plain Error for anything not worth
 * retrying. Transient failures back off and retry; `recoverOffset`, when
 * given, is asked where storage really stands before each retry so no slice
 * is sent twice or skipped.
 */
export async function uploadToSession({
    sessionUri,
    file,
    offset = 0,
    chunkBytes = DEFAULT_CHUNK_BYTES,
    onProgress,
    onCommitted,
    recoverOffset,
    signal,
}) {
    const total = file.size;
    const slice = Math.max(SLICE_ALIGNMENT, chunkBytes - (chunkBytes % SLICE_ALIGNMENT));
    let position = Math.max(0, Math.min(offset, total));
    let attempts = 0;

    const backOffAndRecover = async (why) => {
        if (attempts >= RETRYABLE_ATTEMPTS) throw new Error(why);
        attempts += 1;
        await sleep(Math.min(30000, 1000 * 2 ** (attempts - 1)), signal);
        if (recoverOffset) {
            try {
                const recovered = await recoverOffset();
                if (Number.isFinite(recovered) && recovered >= 0) position = Math.min(recovered, total);
            } catch { /* keep going from where we think we are */ }
        }
    };

    if (onProgress) onProgress(position);

    while (true) {
        const end = Math.min(position + slice, total);
        let outcome;
        try {
            outcome = await putSlice({
                sessionUri,
                blob: end > position ? file.slice(position, end) : null,
                start: position,
                end,
                total,
                onProgress,
                signal,
            });
        } catch (err) {
            if (err instanceof RetryableUploadError) {
                await backOffAndRecover(err.message);
                continue;
            }
            throw err;
        }

        const { status, range } = outcome;
        if (status === 200 || status === 201) {
            if (onProgress) onProgress(total);
            if (onCommitted) onCommitted(total);
            return;
        }
        if (status === 308) {
            const committed = committedFromRange(range);
            const next = committed === null ? end : Math.min(committed, total);
            if (next <= position && end > position) {
                // Storage kept none of the slice: treat like a dropped request.
                await backOffAndRecover('Storage did not accept the upload slice');
                continue;
            }
            position = next;
            attempts = 0;
            if (onCommitted) onCommitted(position);
            continue;
        }
        if (status === 404 || status === 410) throw new UploadSessionGoneError();
        if (status === 0 || status === 408 || status === 429 || status >= 500) {
            await backOffAndRecover(`Upload failed after retries (${status})`);
            continue;
        }
        throw new Error(`Upload failed (${status})`);
    }
}
